import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconUserDollar } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import incomeExpenseService from '../../services/incomeExpenseService';

const BN_DIGITS = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
const toBn = (s) => String(s).replace(/[0-9]/g, (d) => BN_DIGITS[d]);

// Today's date in Bangladesh time (UTC+6), as YYYY-MM-DD — matches how the server groups days.
const dhakaToday = () => new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString().slice(0, 10);

/**
 * Read-only stats for one operator on the income page: what they entered (income and expense,
 * kept SEPARATE — expense is never subtracted from income) and what they earn from it —
 * a fixed wage per day worked, or a percentage of the income they entered.
 * Shows today first, then the selected period.
 */
export default function OperatorPayCard({ officeSpaceId, operatorId, dateFrom, dateTo }) {
    const { t, currentLanguage } = useTranslation();
    const isBn = currentLanguage === 'bn';
    const money = useCallback((n) => {
        const raw = `৳${Number(n || 0).toLocaleString('en-BD', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
        return isBn ? toBn(raw) : raw;
    }, [isBn]);
    const num = useCallback((n) => (isBn ? toBn(n) : String(n)), [isBn]);

    const today = dhakaToday();
    const periodIsToday = dateFrom === today && dateTo === today;

    const [period, setPeriod] = useState(null);
    const [todayData, setTodayData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const base = { officeSpaceId, operatorId };
            const [p, d] = await Promise.all([
                incomeExpenseService.operatorPay({ ...base, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined }),
                incomeExpenseService.operatorPay({ ...base, dateFrom: today, dateTo: today }),
            ]);
            setPeriod(p.data);
            setTodayData(d.data);
        } catch (err) {
            setPeriod(null);
            setTodayData(null);
            notifications.show({ message: err.message || t('operatorPay.loadFailed'), color: 'red' });
        } finally {
            setLoading(false);
        }
    }, [officeSpaceId, operatorId, dateFrom, dateTo, today, t]);

    useEffect(() => { load(); }, [load]);

    const wageLabel = (() => {
        if (!period?.configured) return t('operatorPay.notSet');
        const amt = num(Number(period.wageAmount).toLocaleString('en-BD', { maximumFractionDigits: 2 }));
        if (period.wageType === 'percent') return t('operatorPay.percentOfIncome', { pct: amt });
        const key = period.wagePeriod === 'week' ? 'perWeek' : period.wagePeriod === 'month' ? 'perMonth' : 'perDay';
        return t(`operatorPay.${key}`, { amount: amt });
    })();

    const Stat = ({ label, sub, value, tone = 'zinc' }) => {
        const tones = {
            zinc: 'bg-zinc-50 dark:bg-zinc-800/60 border-zinc-200 dark:border-zinc-700 text-zinc-800 dark:text-zinc-100',
            emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-100 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300',
            red: 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800 text-red-700 dark:text-red-300',
            orange: 'bg-orange-50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-800 text-orange-700 dark:text-orange-300',
        };
        return (
            <div className={`rounded-xl border px-3.5 py-3 ${tones[tone]}`}>
                <p className="text-[11px] font-medium opacity-75">{label}</p>
                <p className="mt-0.5 text-lg font-bold tabular-nums">{value}</p>
                {sub && <p className="mt-0.5 text-[10.5px] opacity-70">{sub}</p>}
            </div>
        );
    };

    // "He earns" = the operator's share from the entries in the range.
    const earnsSub = (d) => {
        if (!d?.configured) return null;
        return d.wageType === 'fixed'
            ? `${money(d.ratePerDay)} × ${num(d.daysWorked)} ${t('operatorPay.daysUnit')}`
            : `${num(d.wageAmount)}% × ${money(d.incomeTotal)}`;
    };

    return (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-700/60 p-4 sm:p-5">
            <div className="flex items-center gap-2.5 min-w-0">
                <span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-primary/10 text-primary"><IconUserDollar size={18} /></span>
                <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                        {t('operatorPay.cardTitle', { name: period?.operator?.name || '' })}
                    </p>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">{wageLabel}</p>
                </div>
            </div>

            {loading && !period ? (
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[0, 1, 2, 3].map((i) => <div key={i} className="h-[74px] animate-pulse rounded-xl bg-zinc-100 dark:bg-zinc-800" />)}
                </div>
            ) : period ? (
                <>
                    {/* Today + selected period side by side: every card sits in ONE row on wide screens
                        (3 + 4 cards), and the two groups stack on narrower ones. */}
                    <div className="mt-4 flex flex-col gap-4 lg:flex-row lg:gap-5">
                        {!periodIsToday && todayData && (
                            <div className="min-w-0 lg:flex-[3]">
                                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{t('operatorPay.today')}</p>
                                <div className="grid grid-cols-3 gap-3">
                                    <Stat label={t('operatorPay.incomeEntered')} value={money(todayData.incomeTotal)} tone="emerald" />
                                    <Stat label={t('operatorPay.expenseEntered')} value={money(todayData.expenseTotal)} tone="red" />
                                    <Stat label={t('operatorPay.earns')} sub={earnsSub(todayData)} value={todayData.configured ? money(todayData.payable) : '—'} tone="orange" />
                                </div>
                            </div>
                        )}
                        <div className="min-w-0 lg:flex-[4]">
                            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
                                {periodIsToday ? t('operatorPay.today') : (dateFrom || dateTo ? t('operatorPay.selectedPeriod') : t('operatorPay.allTime'))}
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                <Stat label={t('operatorPay.incomeEntered')} value={money(period.incomeTotal)} tone="emerald" />
                                <Stat label={t('operatorPay.expenseEntered')} value={money(period.expenseTotal)} tone="red" />
                                {period.wageType === 'fixed'
                                    ? <Stat label={t('operatorPay.daysWorked')} value={num(period.daysWorked)} />
                                    : <Stat label={t('operatorPay.entriesCount')} value={num(period.entryCount)} />}
                                <Stat label={t('operatorPay.earns')} sub={earnsSub(period)} value={period.configured ? money(period.payable) : '—'} tone="orange" />
                            </div>
                        </div>
                    </div>

                    {!period.configured ? (
                        <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 dark:bg-amber-900/15 border border-amber-100 dark:border-amber-800/40 px-4 py-3">
                            <p className="text-sm text-amber-700 dark:text-amber-300">{t('operatorPay.noWage')}</p>
                            <Link to={`/officespaces/${officeSpaceId}/settings`} className="text-[13px] font-semibold text-primary hover:underline">
                                {t('operatorPay.setWage')} →
                            </Link>
                        </div>
                    ) : (
                        <p className="mt-3 text-[11px] text-zinc-400 dark:text-zinc-500">{t('operatorPay.expenseNote')}</p>
                    )}
                </>
            ) : null}
        </div>
    );
}
